import { AiFieldType, DueDiligenceFunnelTierSectionPdfLayout, SettingsPageGetDueDiligenceFunnelQuery } from '@/__generated__/graphql';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Input from '@/components/basicComponents/input';
import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CREATE_DUE_DILIGENCE_SECTION_MUTATION, GET_FUNNEL_QUERY } from '../queries';
import { saveOnEnter } from '../utils';

interface DuplicateSectionProps {
  onClose: () => void;
  isOpen: boolean;
  tiersData: SettingsPageGetDueDiligenceFunnelQuery['getDueDiligenceFunnel']['tiers'];
  tierId: string;
  currentSection: SettingsPageGetDueDiligenceFunnelQuery['getDueDiligenceFunnel']['tiers'][number]['sections'][number] | null;
}

const DuplicateSection = ({ onClose, isOpen, tiersData, tierId, currentSection }: DuplicateSectionProps) => {
  if (!currentSection) return <></>;
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [value, setValue] = useState('');

  const [createSection] = useMutation(CREATE_DUE_DILIGENCE_SECTION_MUTATION, {
    refetchQueries: () => [
      {
        query: GET_FUNNEL_QUERY,
        variables: {
          id: queryParams.get('funnelId')
        }
      }
    ]
  });

  const saveValue = () => {
    const updatedFields = currentSection.fields.map((field) => {
      return {
        name: field.name,
        required: field.required,
        type: field.type as AiFieldType,
        linkedFieldId: null as null,
        promptGroup: field.promptGroup
          ? {
              prompts: field.promptGroup.prompts,
              summaryPrompt: field.promptGroup.summaryPrompt,
              contextPrompt: field.promptGroup.contextPrompt,
              rubricTemplates: field.promptGroup.rubricTemplates
            }
          : null
      };
    });
    const numberOfSections = tiersData.find((tier) => tier.id === tierId)?.sections.length ?? 0;

    createSection({
      variables: {
        data: {
          funnelTierId: tierId,
          name: value,
          userCanScore: currentSection.userCanScore,
          order: numberOfSections + 1,
          pdfLayout: currentSection.pdfLayout as DueDiligenceFunnelTierSectionPdfLayout,
          fields: updatedFields
        }
      }
    });

    onClose();
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Duplicate Section"
      confirmButton={{ name: 'Save', onClick: saveValue, disabled: !value }}
    >
      <Input
        value={value}
        onChange={(event) => setValue(event.target.value)}
        label="Name"
        type="text"
        placeholder="Give this Section a unique name"
        size="lg"
        onKeyUp={(event) => saveOnEnter(event, saveValue, !value)}
        isRequiredField
        autoFocus
      />
    </ModalWrappedContent>
  );
};

export default DuplicateSection;
